import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SchemaOrg from '@/components/base/SchemaOrg';
import { categories, featuredArticles } from '@/mocks/home';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'all');

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  const activeCategory = categories.find((c) => c.slug === activeSlug) || categories[0];

  const filteredArticles = activeSlug === 'all'
    ? featuredArticles
    : featuredArticles.filter((a) => a.category === activeSlug);

  const breadcrumbSchema = {
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://giaigfd.cn' },
      { '@type': 'ListItem', position: 2, name: activeCategory.name },
    ],
  };

  return (
    <>
      <SchemaOrg type="BreadcrumbList" data={breadcrumbSchema} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <section className="bg-background-100 py-14 md:py-18 px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6">
                <Link to="/" className="hover:text-primary-600 transition-colors duration-200 cursor-pointer">首页</Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <span className="text-foreground-800">{activeCategory.name}</span>
              </nav>
              <h1 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-2">
                {activeCategory.name}
              </h1>
              <p className="text-foreground-600 text-sm md:text-base">
                共 {filteredArticles.length} 篇相关内容
              </p>
            </div>
          </section>

          <section className="py-10 px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <div className="flex flex-wrap gap-2 mb-10">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    onClick={() => setActiveSlug(cat.slug)}
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                      activeSlug === cat.slug
                        ? 'bg-primary-500 text-white'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    <i className={`${cat.icon} text-xs`}></i>
                    {cat.name}
                    <span className={`text-xs ${activeSlug === cat.slug ? 'text-white/70' : 'text-foreground-400'}`}>
                      {cat.count}
                    </span>
                  </Link>
                ))}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {filteredArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    className="group bg-background-50 rounded-2xl overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                  >
                    <div className="relative h-48 md:h-52 overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                      <span className="absolute top-3 left-3 px-3 py-1 rounded-full bg-white/90 backdrop-blur-sm text-xs font-medium text-foreground-800">
                        {article.categoryName}
                      </span>
                    </div>
                    <div className="p-5 md:p-6">
                      <h2 className="font-heading text-base md:text-lg text-foreground-950 mb-2.5 line-clamp-2 group-hover:text-primary-600 transition-colors duration-200">
                        {article.title}
                      </h2>
                      <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2 mb-4">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center justify-between text-xs text-foreground-500">
                        <span>{article.author}</span>
                        <div className="flex items-center gap-2">
                          <span>{article.date}</span>
                          <span>{article.views.toLocaleString()} 阅读</span>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>

              {filteredArticles.length === 0 && (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                    <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-500">该分类暂无内容</p>
                </div>
              )}
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}